import { AxiosInstance, AxiosResponse } from "axios";
import { CompanyModel } from "@/models/CompanyModel";
import { TwilioSettingsModel } from "@/models/TwilioSettingsModel";
import Shared from "@/common/shared";
import TwilioConfigurationModel, { ConfigurationModel } from "@/models/TwilioConfigurationModel";
import TwilioCredentialsModel from "@/models/TwilioCredentialsModel";

export default class CompanyService {
  private axiosInstance: AxiosInstance;

  public constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  public async getCompanyList(): Promise<CompanyModel[]> {
    const url = `/Company/list`;
    const res: AxiosResponse<CompanyModel[]> = await this.axiosInstance.get<CompanyModel[]>(url);
    return res.data;
  }

  public async getCompanyDetails(): Promise<CompanyModel | null> {
    const url = `/Company`;
    const res: AxiosResponse<CompanyModel> = await this.axiosInstance.get<CompanyModel>(url);
    return res.data;
  }

  public async createCompany(company: CompanyModel): Promise<CompanyModel> {
    const url = `/Company/create`;
    const res: AxiosResponse<CompanyModel | string> = await this.axiosInstance.post<CompanyModel | string>(
      url,
      company,
    );
    if (res.status === 409) {
      const conflictError = new Error();
      conflictError.name = Shared.conflict;
      conflictError.message = res.data as string;
      return Promise.reject(conflictError);
    }
    return res.data as CompanyModel;
  }

  public async updateCompany(company: CompanyModel): Promise<CompanyModel> {
    const url = `/Company/update`;
    const res: AxiosResponse<CompanyModel | string> = await this.axiosInstance.post<CompanyModel | string>(
      url,
      company,
    );
    if (res.status === 409) {
      const conflictError = new Error();
      conflictError.name = Shared.conflict;
      conflictError.message = res.data as string;
      return Promise.reject(conflictError);
    }
    return res.data as CompanyModel;
  }

  public async getTwilioSettings(): Promise<TwilioSettingsModel[]> {
    const url = `/Company/twilio-settings`;
    const res: AxiosResponse<TwilioSettingsModel[]> = await this.axiosInstance.get<TwilioSettingsModel[]>(url);
    return res.data;
  }

  public async upsertTwilioSettings(settings: TwilioSettingsModel[]): Promise<TwilioSettingsModel[]> {
    const url = `/Company/twilio-settings`;
    const res: AxiosResponse<TwilioSettingsModel[]> = await this.axiosInstance.post<TwilioSettingsModel[]>(
      url,
      settings,
    );
    return res.data;
  }

  public async getTwilioConfigurations(twilioCreddentials: TwilioCredentialsModel): Promise<TwilioConfigurationModel> {
    const url = `/Company/twilio-configurations`;
    const res: AxiosResponse<TwilioConfigurationModel> = await this.axiosInstance.post<TwilioConfigurationModel>(
      url,
      twilioCreddentials,
    );
    return res.data;
  }

  public async getTwilioWorkflows(
    twilioCreddentials: TwilioCredentialsModel,
    workspaceSid: string,
  ): Promise<ConfigurationModel[]> {
    const url = `/Company/twilio-workflows/${workspaceSid}`;
    const res: AxiosResponse<ConfigurationModel[]> = await this.axiosInstance.post<ConfigurationModel[]>(
      url,
      twilioCreddentials,
    );
    return res.data;
  }
}
